import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  LinearProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Switch,
  Alert,
  Checkbox,
  InputAdornment,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  HelpCircle,
  FileQuestion,
  Layers,
  BarChart3,
  Plus,
  Upload,
  Edit2,
  Trash2,
  Download,
  Search,
  Clock,
  Calendar,
  CheckCircle2,
  XCircle,
  Award,
  Users,
  Eye,
  RotateCcw,
  MapPin,
  X,
  Wifi,
  WifiOff,
  AlertTriangle,
  RefreshCw,
  Activity,
  Cpu,
  Server,
  Radio as RadioIcon
} from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { GlassCard } from '../components/GlassCard';
import { TiptapQuestionEditor } from '../components/TiptapQuestionEditor';
import { SatelliteMapPicker } from '../components/SatelliteMapPicker';

export const ComprehensiveExamManager: React.FC = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSupervisor = user.role === 'SUPERVISOR';
  const supervisorDept = (user.department || user.major || '').trim();

  // If Supervisor, lock view to Results Tab (1)
  const [activeTab, setActiveTab] = useState<number>(isSupervisor ? 1 : 0);
  const [loading, setLoading] = useState<boolean>(false);

  // Question Bank States
  const [questions, setQuestions] = useState<any[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('ALL');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [qPage, setQPage] = useState<number>(0);
  const [qRowsPerPage, setQRowsPerPage] = useState<number>(10);

  // Question Dialog
  const [openQuestionDialog, setOpenQuestionDialog] = useState<boolean>(false);
  const [editingQuestion, setEditingQuestion] = useState<any>(null);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    correct_answer: 'A',
    explanation: '',
    course_id: '',
    academic_year: '2569',
    points: 1.0,
  });

  // CSV Import Dialog
  const [openCsvDialog, setOpenCsvDialog] = useState<boolean>(false);
  const [csvPreviewData, setCsvPreviewData] = useState<any[]>([]);
  const [csvFileName, setCsvFileName] = useState<string>('');
  const [csvError, setCsvError] = useState<string>('');

  // Exam Sets States
  const [examSets, setExamSets] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [openSetDialog, setOpenSetDialog] = useState<boolean>(false);
  const [editingSet, setEditingSet] = useState<any>(null);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [setForm, setSetForm] = useState({
    title: '',
    description: '',
    total_points: 100,
    course_id: '',
    duration_minutes: 60,
    allowed_tab_switches: 3,
    require_geofence: false,
    latitude: 16.48278,
    longitude: 99.52222,
    radius_meters: 500,
    passing_percentage: 60.0,
    academic_year: '2569',
    semester: '1',
    start_datetime: '',
    end_datetime: '',
    is_published: true,
  });

  // Question Selector Filters inside Exam Set Dialog (Requirement 2)
  const [selectorCourseFilter, setSelectorCourseFilter] = useState<string>('ALL');
  const [selectorYearFilter, setSelectorYearFilter] = useState<string>('ALL');
  const [selectorSearch, setSelectorSearch] = useState<string>('');

  // Results & Reporting States
  const [selectedSetForReport, setSelectedSetForReport] = useState<string>('');
  const [reportData, setReportData] = useState<any>({ exam_set: null, attempts: [] });
  const [loadingReport, setLoadingReport] = useState<boolean>(false);
  const [resultsFilter, setResultsFilter] = useState<'ALL' | 'PASSED' | 'FAILED'>('ALL');
  const [resultsSearch, setResultsSearch] = useState<string>('');
  const [resultsPage, setResultsPage] = useState<number>(0);
  const [resultsRowsPerPage, setResultsRowsPerPage] = useState<number>(10);
  const isAdminOrStaff = ['ADMIN', 'STAFF'].includes(user.role);
  const [editScoreDialog, setEditScoreDialog] = useState<{ open: boolean; attempt: any; score: number | string }>({
    open: false,
    attempt: null,
    score: 0,
  });

  // Term & Academic Year filters for Tab 1 (Results & Live Monitor)
  const [filterSemester, setFilterSemester] = useState<string>('ALL');
  const [filterAcademicYear, setFilterAcademicYear] = useState<string>('ALL');

  // Sub-view toggle in Tab 1: 'RESULTS' | 'LIVE_MONITOR'
  const [reportViewMode, setReportViewMode] = useState<'RESULTS' | 'LIVE_MONITOR'>('RESULTS');

  // Real-time Live Proctoring Monitor state
  const [liveMonitorData, setLiveMonitorData] = useState<any>(null);
  const [loadingLiveMonitor, setLoadingLiveMonitor] = useState<boolean>(false);
  const [autoRefreshMonitor, setAutoRefreshMonitor] = useState<boolean>(true);
  const [monitorSearch, setMonitorSearch] = useState<string>('');
  const [monitorFilterStatus, setMonitorFilterStatus] = useState<string>('ALL');
  const [monitorPage, setMonitorPage] = useState<number>(0);
  const [monitorRowsPerPage, setMonitorRowsPerPage] = useState<number>(25);

  // Fetch Questions
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const res = await api.get('/exams/questions');
      setQuestions(res.data.questions || []);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Exam Sets
  const fetchExamSets = async () => {
    try {
      setLoading(true);
      const res = await api.get('/exams/sets');
      setExamSets(res.data || []);
      const defaultSetId = res.data && res.data.length > 0 ? res.data[0].id : '';
      if (defaultSetId) {
        if (!selectedSetForReport) {
          setSelectedSetForReport(defaultSetId);
        }
        if (isSupervisor) {
          fetchSetResults(defaultSetId);
        }
      }
    } catch (err) {
      console.error('Failed to fetch exam sets:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Courses
  const fetchCourses = async () => {
    try {
      const res = await api.get('/courses');
      setCourses(res.data || []);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
    }
  };

  // Fetch Results for a set
  const fetchSetResults = async (setId: string) => {
    if (!setId) return;
    try {
      setLoadingReport(true);
      setResultsPage(0);
      const res = await api.get(`/exams/sets/${setId}/results`);
      setReportData(res.data || { exam_set: null, attempts: [] });
    } catch (err) {
      console.error('Failed to fetch exam set results:', err);
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (!isSupervisor) {
      fetchQuestions();
    }
    fetchExamSets();
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupervisor]);

  // Initial Load: Fetch active system settings to set current semester and academic year
  useEffect(() => {
    api.get('/settings').then((res) => {
      const s = res.data || {};
      if (s.current_semester) setFilterSemester(String(s.current_semester));
      if (s.current_academic_year) setFilterAcademicYear(String(s.current_academic_year));
    }).catch(() => {});
  }, []);

  // Filter exam sets for Results & Live Monitor based on semester and academic year
  const filteredExamSetsForReport = useMemo(() => {
    return examSets.filter((es) => {
      const matchSem = filterSemester === 'ALL' || String(es.semester) === filterSemester;
      const matchYear = filterAcademicYear === 'ALL' || String(es.academic_year) === filterAcademicYear;
      return matchSem && matchYear;
    });
  }, [examSets, filterSemester, filterAcademicYear]);

  // Auto-select first exam set in the filtered list if selected set is not in it
  useEffect(() => {
    if (filteredExamSetsForReport.length > 0) {
      const exists = filteredExamSetsForReport.some((s) => s.id === selectedSetForReport);
      if (!exists) {
        setSelectedSetForReport(filteredExamSetsForReport[0].id);
      }
    }
  }, [filteredExamSetsForReport, selectedSetForReport]);

  // Fetch Real-time Monitor for a set
  const fetchLiveMonitor = async (setId = selectedSetForReport) => {
    if (!setId) return;
    try {
      setLoadingLiveMonitor(true);
      const res = await api.get(`/exams/sets/${setId}/live-monitor`);
      setLiveMonitorData(res.data);
    } catch (err) {
      console.error('Failed to fetch live exam monitor:', err);
    } finally {
      setLoadingLiveMonitor(false);
    }
  };

  useEffect(() => {
    if ((isSupervisor || activeTab === 1) && selectedSetForReport) {
      if (reportViewMode === 'RESULTS') {
        fetchSetResults(selectedSetForReport);
      } else if (reportViewMode === 'LIVE_MONITOR') {
        fetchLiveMonitor(selectedSetForReport);
      }
    }
  }, [activeTab, selectedSetForReport, isSupervisor, reportViewMode]);

  // Polling Interval for Live Monitor (every 10 seconds)
  useEffect(() => {
    if ((isSupervisor || activeTab === 1) && reportViewMode === 'LIVE_MONITOR' && selectedSetForReport && autoRefreshMonitor) {
      const timer = setInterval(() => {
        fetchLiveMonitor(selectedSetForReport);
      }, 10000);
      return () => clearInterval(timer);
    }
  }, [activeTab, isSupervisor, reportViewMode, selectedSetForReport, autoRefreshMonitor]);

  // Available Academic Years for Questions & Exam Sets
  const availableQuestionYears = useMemo(() => {
    const yearsSet = new Set<string>();
    questions.forEach((q) => {
      if (q.academic_year) yearsSet.add(String(q.academic_year));
    });
    ['2570', '2569', '2568', '2567'].forEach((y) => yearsSet.add(y));
    return Array.from(yearsSet).sort((a, b) => Number(b) - Number(a));
  }, [questions]);

  // Filtered Questions for Question Bank (Tab 0)
  const filteredQuestions = useMemo(() => {
    return questions.filter((q) => {
      const matchCourse =
        selectedCourseId === 'ALL' ||
        (selectedCourseId === 'NONE' ? !q.course_id : q.course_id === selectedCourseId);
      const matchYear =
        selectedAcademicYear === 'ALL' ||
        (selectedAcademicYear === 'NONE' ? !q.academic_year : String(q.academic_year) === selectedAcademicYear);
      const query = searchQuery.trim().toLowerCase();
      const matchSearch =
        !query ||
        q.question_text?.toLowerCase().includes(query) ||
        q.explanation?.toLowerCase().includes(query) ||
        q.course_code?.toLowerCase().includes(query) ||
        q.course_name?.toLowerCase().includes(query);
      return matchCourse && matchYear && matchSearch;
    });
  }, [questions, selectedCourseId, selectedAcademicYear, searchQuery]);

  // Filtered Questions for Exam Set Question Selector (Dialog 3 - Requirement 2)
  const selectorFilteredQuestions = useMemo(() => {
    return questions.filter((q) => {
      const matchCourse =
        selectorCourseFilter === 'ALL' ||
        (selectorCourseFilter === 'NONE' ? !q.course_id : q.course_id === selectorCourseFilter);
      const matchYear =
        selectorYearFilter === 'ALL' ||
        (selectorYearFilter === 'NONE' ? !q.academic_year : String(q.academic_year) === selectorYearFilter);
      const query = selectorSearch.trim().toLowerCase();
      const matchSearch =
        !query ||
        q.question_text?.toLowerCase().includes(query) ||
        q.course_code?.toLowerCase().includes(query) ||
        q.course_name?.toLowerCase().includes(query);
      return matchCourse && matchYear && matchSearch;
    });
  }, [questions, selectorCourseFilter, selectorYearFilter, selectorSearch]);

  // Question CRUD Handlers
  const handleOpenQuestionDialog = (q: any = null) => {
    if (q) {
      setEditingQuestion(q);
      setQuestionForm({
        question_text: q.question_text || '',
        option_a: q.option_a || '',
        option_b: q.option_b || '',
        option_c: q.option_c || '',
        option_d: q.option_d || '',
        correct_answer: q.correct_answer || 'A',
        explanation: q.explanation || '',
        course_id: q.course_id || '',
        academic_year: q.academic_year ? String(q.academic_year) : '2569',
        points: q.points || 1.0,
      });
    } else {
      setEditingQuestion(null);
      setQuestionForm({
        question_text: '',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        correct_answer: 'A',
        explanation: '',
        course_id: selectedCourseId !== 'ALL' && selectedCourseId !== 'NONE' ? selectedCourseId : '',
        academic_year: selectedAcademicYear !== 'ALL' && selectedAcademicYear !== 'NONE' ? selectedAcademicYear : '2569',
        points: 1.0,
      });
    }
    setOpenQuestionDialog(true);
  };

  const handleSaveQuestion = async () => {
    if (!questionForm.question_text.trim() || !questionForm.option_a.trim() || !questionForm.option_b.trim()) {
      alert('กรุณากรอกโจทย์คำถามและตัวเลือก ก - ข เป็นอย่างน้อย');
      return;
    }
    try {
      if (editingQuestion) {
        await api.put(`/exams/questions/${editingQuestion.id}`, questionForm);
      } else {
        await api.post('/exams/questions', questionForm);
      }
      setOpenQuestionDialog(false);
      fetchQuestions();
    } catch (err: any) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อสอบ');
    }
  };

  const handleDeleteQuestion = async (id: string) => {
    if (!window.confirm('ยืนยันลบข้อสอบนี้ออกจากคลังข้อสอบหรือไม่?')) return;
    try {
      await api.delete(`/exams/questions/${id}`);
      fetchQuestions();
    } catch (err: any) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบข้อสอบ');
    }
  };

  // CSV Import Handlers
  const handleDownloadCsvTemplate = () => {
    const csvContent =
      '\uFEFF' +
      'question_text,option_a,option_b,option_c,option_d,correct_answer,explanation,course_code,academic_year,points\n' +
      '"ข้อใดกล่าวถึง Active Learning ได้ถูกต้อง?","ครูบรรยายเป็นหลัก","ผู้เรียนลงมือปฏิบัติและคิดวิเคราะห์","ท่องจำตามตำรา","ทำแบบฝึกหัดซ้ำๆ","B","เน้นกระบวนการที่ผู้เรียนสร้างความรู้ด้วยตนเอง","1002101",2569,1\n' +
      '"เป้าหมายหลักของการวิจัยในชั้นเรียนคือ?","สร้างทฤษฎีใหม่","ขอรับทุนสนับสนุน","แก้ไขปัญหาและพัฒนาการเรียนรู้ของผู้เรียน","จัดอันดับโรงเรียน","C","มุ่งแก้ปัญหาจริงในชั้นเรียน","",2569,1\n';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_exam_questions.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCsvError('');
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (!results.data || results.data.length === 0) {
          setCsvError('ไม่พบข้อมูลในไฟล์ CSV');
          return;
        }
        setCsvPreviewData(results.data);
      },
      error: (error) => {
        setCsvError('เกิดข้อผิดพลาดในการอ่านไฟล์: ' + error.message);
      },
    });
  };

  const handleImportCsv = async () => {
    if (csvPreviewData.length === 0) return;
    try {
      setLoading(true);
      await api.post('/exams/questions/upload-csv', { questions: csvPreviewData });
      alert(`นำเข้าข้อสอบสำเร็จจำนวน ${csvPreviewData.length} ข้อ`);
      setOpenCsvDialog(false);
      setCsvPreviewData([]);
      setCsvFileName('');
      fetchQuestions();
    } catch (err: any) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการนำเข้าข้อสอบ');
    } finally {
      setLoading(false);
    }
  };

  // Exam Set Handlers
  const handleOpenSetDialog = async (set: any = null) => {
    setSelectorSearch('');
    if (set) {
      setEditingSet(set);
      setSelectorCourseFilter(set.course_id || 'ALL');
      setSelectorYearFilter(set.academic_year ? String(set.academic_year) : 'ALL');
      // Fetch full set details with question IDs
      try {
        const res = await api.get(`/exams/sets/${set.id}`);
        const setDetails = res.data;
        setSelectedQuestionIds(setDetails.questions?.map((q: any) => q.id) || []);
        setSetForm({
          title: setDetails.title,
          description: setDetails.description || '',
          total_points: setDetails.total_points !== undefined && setDetails.total_points !== null ? Number(setDetails.total_points) : 100,
          course_id: setDetails.course_id || '',
          duration_minutes: setDetails.duration_minutes || 60,
          allowed_tab_switches: setDetails.allowed_tab_switches !== undefined && setDetails.allowed_tab_switches !== null ? Number(setDetails.allowed_tab_switches) : 3,
          require_geofence: Boolean(setDetails.require_geofence),
          latitude: setDetails.latitude !== null && setDetails.latitude !== undefined ? Number(setDetails.latitude) : 16.48278,
          longitude: setDetails.longitude !== null && setDetails.longitude !== undefined ? Number(setDetails.longitude) : 99.52222,
          radius_meters: setDetails.radius_meters || 500,
          passing_percentage: setDetails.passing_percentage || 60.0,
          academic_year: String(setDetails.academic_year || '2569'),
          semester: String(setDetails.semester || '1'),
          start_datetime: setDetails.start_datetime ? setDetails.start_datetime.slice(0, 16) : '',
          end_datetime: setDetails.end_datetime ? setDetails.end_datetime.slice(0, 16) : '',
          is_published: Boolean(setDetails.is_published),
        });
      } catch (e) {
        console.error(e);
      }
    } else {
      setEditingSet(null);
      setSelectedQuestionIds([]);
      const defaultCourseId = courses[0]?.id || '';
      setSelectorCourseFilter(defaultCourseId || 'ALL');
      setSelectorYearFilter('2569');
      setSetForm({
        title: '',
        description: '',
        total_points: 100,
        course_id: defaultCourseId,
        duration_minutes: 60,
        allowed_tab_switches: 3,
        require_geofence: false,
        latitude: 16.48278,
        longitude: 99.52222,
        radius_meters: 500,
        passing_percentage: 60.0,
        academic_year: '2569',
        semester: '1',
        start_datetime: '',
        end_datetime: '',
        is_published: true,
      });
    }
    setOpenSetDialog(true);
  };

  const handleSaveSet = async () => {
    if (!setForm.title.trim()) {
      alert('กรุณากรอกชื่อชุดข้อสอบ');
      return;
    }
    if (selectedQuestionIds.length === 0) {
      alert('กรุณาเลือกข้อสอบจากคลังข้อสอบอย่างน้อย 1 ข้อสำหรับชุดข้อสอบนี้');
      return;
    }

    const payload = {
      ...setForm,
      question_ids: selectedQuestionIds,
    };

    try {
      if (editingSet) {
        await api.put(`/exams/sets/${editingSet.id}`, payload);
      } else {
        await api.post('/exams/sets', payload);
      }
      setOpenSetDialog(false);
      fetchExamSets();
    } catch (err: any) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกชุดข้อสอบ');
    }
  };

  const handleDeleteSet = async (id: string) => {
    if (!window.confirm('ยืนยันลบชุดข้อสอบนี้หรือไม่? ข้อมูลการสอบที่เกี่ยวข้องจะถูกลบออกด้วย')) return;
    try {
      await api.delete(`/exams/sets/${id}`);
      fetchExamSets();
    } catch (err: any) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบชุดข้อสอบ');
    }
  };

  const handleToggleQuestionInSet = (qId: string) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(qId) ? prev.filter((id) => id !== qId) : [...prev, qId]
    );
  };

  // Filter attempts for results tab (locked to supervisor's major if role is SUPERVISOR)
  const displayAttempts = useMemo(() => {
    const atts = reportData.attempts || [];
    if (!isSupervisor || !supervisorDept) return atts;
    const cleanSup = supervisorDept.includes('(')
      ? supervisorDept.split('(')[1].split(')')[0].trim()
      : supervisorDept.replace('วิทยาศาสตร์ศึกษา', '').replace('การศึกษา', '').replace('ศึกษา', '').trim();

    return atts.filter((a: any) => {
      const stdMajor = (a.major || '').trim();
      if (!stdMajor) return false;
      if (stdMajor === supervisorDept) return true;
      const cleanStd = stdMajor.includes('(')
        ? stdMajor.split('(')[1].split(')')[0].trim()
        : stdMajor.replace('วิทยาศาสตร์ศึกษา', '').replace('การศึกษา', '').replace('ศึกษา', '').trim();
      return cleanSup === cleanStd;
    });
  }, [reportData.attempts, isSupervisor, supervisorDept]);

  // Admin / Staff Edit Score handler
  const handleSaveEditedScore = async () => {
    if (!editScoreDialog.attempt) return;
    try {
      await api.put(`/exams/attempts/${editScoreDialog.attempt.id}/score`, {
        score: Number(editScoreDialog.score),
      });
      alert('แก้ไขคะแนนสอบเรียบร้อยแล้ว');
      setEditScoreDialog({ open: false, attempt: null, score: 0 });
      fetchSetResults(selectedSetForReport);
      fetchExamSets();
    } catch (err: any) {
      alert(err.response?.data?.message || 'ไม่สามารถแก้ไขคะแนนได้');
    }
  };

  // Admin / Staff Reset Attempt handler
  const handleResetAttempt = async (attempt: any) => {
    if (!isAdminOrStaff) {
      alert('ขออภัย สิทธิ์ในการรีเซ็ตผลการสอบจำกัดเฉพาะผู้ดูแลระบบและเจ้าหน้าที่เท่านั้น');
      return;
    }
    const studentName = `${attempt.prefix || ''}${attempt.first_name || ''} ${attempt.last_name || ''}`.trim();
    if (
      !window.confirm(
        `ยืนยันการรีเซ็ตผลการสอบของนักศึกษา "${studentName}" หรือไม่?\n\nระบบจะล้างคะแนน ผลการสอบ และสถิติการเข้าสอบทั้งหมด เพื่อให้นักศึกษาสามารถเข้าสอบใหม่ได้ทันที`
      )
    ) {
      return;
    }

    try {
      await api.delete(`/exams/attempts/${attempt.id}`);
      alert('รีเซ็ตผลการสอบเรียบร้อยแล้ว');
      fetchSetResults(selectedSetForReport);
      fetchExamSets();
    } catch (err: any) {
      alert(err.response?.data?.message || 'ไม่สามารถรีเซ็ตผลการสอบได้');
    }
  };

  // Export Results to Excel
  const handleExportResultsExcel = () => {
    if (!reportData.exam_set || !displayAttempts || displayAttempts.length === 0) {
      alert('ไม่มีข้อมูลผลการสอบสำหรับการส่งออก');
      return;
    }

    const rows = displayAttempts.map((a: any, idx: number) => ({
      'ลำดับ': idx + 1,
      'รหัสนักศึกษา': a.student_code || '-',
      'ชื่อ-สกุล': `${a.prefix || ''}${a.first_name || ''} ${a.last_name || ''}`.trim(),
      'สาขาวิชา': a.major || '-',
      'โรงเรียนฝึกประสบการณ์': a.school_name || '-',
      'ชุดข้อสอบ': reportData.exam_set.title,
      'คะแนนที่ได้': Number(a.score),
      'คะแนนเต็ม': Number(a.total_points),
      'ร้อยละ (%)': Number(a.percentage).toFixed(2),
      'เกณฑ์ผ่าน': `${reportData.exam_set.passing_percentage}%`,
      'ผลการประเมิน': a.is_passed ? 'ผ่านเกณฑ์' : 'ไม่ผ่านเกณฑ์',
      'สถานะการส่ง': a.status === 'TIMED_OUT' ? 'หมดเวลาสอบ' : 'ส่งข้อสอบแล้ว',
      'วันที่สอบ': a.submitted_at ? new Date(a.submitted_at).toLocaleString('th-TH') : '-',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Exam_Results');
    XLSX.writeFile(
      wb,
      `ผลการสอบประมวลผลความรู้_${reportData.exam_set.title.slice(0, 30)}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  // Analytics for results tab
  const totalAttempts = displayAttempts?.length || 0;
  const passedCount = displayAttempts?.filter((a: any) => a.is_passed).length || 0;
  const failedCount = totalAttempts - passedCount;
  const avgPercentage =
    totalAttempts > 0
      ? (
          displayAttempts.reduce((acc: number, curr: any) => acc + Number(curr.percentage || 0), 0) /
          totalAttempts
        ).toFixed(1)
      : '0.0';

  // Filtered and paginated attempts based on interactive card clicks & search
  const filteredAttempts = useMemo(() => {
    if (!displayAttempts) return [];
    let list = displayAttempts;
    if (resultsFilter === 'PASSED') {
      list = list.filter((a: any) => a.is_passed);
    } else if (resultsFilter === 'FAILED') {
      list = list.filter((a: any) => !a.is_passed);
    }

    if (resultsSearch.trim()) {
      const q = resultsSearch.trim().toLowerCase();
      list = list.filter((a: any) => {
        const studentCode = (a.student_code || '').toLowerCase();
        const fullName = `${a.prefix || ''}${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        const major = (a.major || '').toLowerCase();
        const schoolName = (a.school_name || '').toLowerCase();
        return studentCode.includes(q) || fullName.includes(q) || major.includes(q) || schoolName.includes(q);
      });
    }

    return list;
  }, [displayAttempts, resultsFilter, resultsSearch]);

  const paginatedAttempts = useMemo(() => {
    const start = resultsPage * resultsRowsPerPage;
    return filteredAttempts.slice(start, start + resultsRowsPerPage);
  }, [filteredAttempts, resultsPage, resultsRowsPerPage]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
            COMPREHENSIVE EXAMINATION MANAGEMENT SYSTEM
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A' }}>
            ระบบสอบประมวลผลความรู้
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isSupervisor
              ? 'รายงานผลการสอบและสถิติคะแนนสอบประมวลผลความรู้ของนักศึกษา คณะครุศาสตร์ มรภ.กำแพงเพชร'
              : 'บริหารจัดการคลังข้อสอบ กำหนดชุดข้อสอบตามรายวิชาฝึกประสบการณ์ฯ กำหนดระยะเวลาเปิด-ปิด และประมวลผลคะแนนการสอบ คณะครุศาสตร์ มรภ.กำแพงเพชร'}
          </Typography>
        </Box>
      </Box>

      {/* Navigation Tabs - Hidden for Supervisor (only Results & Stats available) */}
      {!isSupervisor ? (
        <Paper sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 2, borderBottom: '1px solid #E2E8F0' }}
          >
            <Tab
              icon={<Layers size={18} />}
              iconPosition="start"
              label={`ชุดข้อสอบ & จับคู่รายวิชา (${examSets.length})`}
              sx={{ fontWeight: 700, py: 2 }}
            />
            <Tab
              icon={<BarChart3 size={18} />}
              iconPosition="start"
              label="รายงานผลการสอบ & สถิติ"
              sx={{ fontWeight: 700, py: 2 }}
            />
            <Tab
              icon={<HelpCircle size={18} />}
              iconPosition="start"
              label={`คลังข้อสอบ (${questions.length})`}
              sx={{ fontWeight: 700, py: 2 }}
            />
          </Tabs>
        </Paper>
      ) : (
        <Paper
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <BarChart3 size={24} color="#1E40AF" />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A8A' }}>
                รายงานผลการสอบ & สถิติ (เฉพาะนักศึกษาในสาขาวิชาที่นิเทศ)
              </Typography>
              <Typography variant="caption" sx={{ color: '#2563EB', display: 'block' }}>
                แสดงผลคะแนนและสถิติการสอบประมวลผลความรู้ของนักศึกษาสาขาวิชา {supervisorDept || 'ที่สังกัด'}
              </Typography>
            </Box>
          </Box>
          {supervisorDept && (
            <Chip
              label={`สาขาวิชา: ${supervisorDept}`}
              size="small"
              sx={{ bgcolor: '#1E40AF', color: '#FFFFFF', fontWeight: 700 }}
            />
          )}
        </Paper>
      )}

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ======================================================== */}
      {/* TAB 2: QUESTION BANK (คลังข้อสอบ) */}
      {/* ======================================================== */}
      {!isSupervisor && activeTab === 2 && (
        <Box>
          <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={1.5} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="ค้นหาโจทย์ข้อสอบ, คำอธิบายเฉลย, รายวิชา..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setQPage(0);
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={18} color="#1E40AF" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3.5, md: 2.5 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>รายวิชา</InputLabel>
                  <Select
                    value={selectedCourseId}
                    label="รายวิชา"
                    onChange={(e) => {
                      setSelectedCourseId(e.target.value);
                      setQPage(0);
                    }}
                  >
                    <MenuItem value="ALL">ทุกรายวิชา</MenuItem>
                    <MenuItem value="NONE">ไม่ระบุรายวิชา (ชุดกลาง)</MenuItem>
                    {courses.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.course_code} - {c.course_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, sm: 2.5, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>ปีการศึกษา</InputLabel>
                  <Select
                    value={selectedAcademicYear}
                    label="ปีการศึกษา"
                    onChange={(e) => {
                      setSelectedAcademicYear(e.target.value);
                      setQPage(0);
                    }}
                  >
                    <MenuItem value="ALL">ทุกปี</MenuItem>
                    {availableQuestionYears.map((y) => (
                      <MenuItem key={y} value={String(y)}>
                        ปี {y}
                      </MenuItem>
                    ))}
                    <MenuItem value="NONE">ไม่ระบุปี</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2.5 }} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<Upload size={16} />}
                  onClick={() => setOpenCsvDialog(true)}
                  sx={{ borderRadius: 2, fontWeight: 600, py: 0.8 }}
                >
                  นำเข้า CSV
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Plus size={16} />}
                  onClick={() => handleOpenQuestionDialog()}
                  sx={{ borderRadius: 2, fontWeight: 600, py: 0.8 }}
                >
                  เพิ่มข้อสอบ
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Question List Table */}
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 15px rgba(0,0,0,0.04)' }}>
            <Table>
              <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 50 }}>ลำดับ</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 280 }}>โจทย์คำถาม</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 180 }}>รายวิชา</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, width: 95 }}>ปีการศึกษา</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, width: 75 }}>เฉลย</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, width: 75 }}>คะแนน</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, width: 90 }}>จัดการ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQuestions
                  .slice(qPage * qRowsPerPage, qPage * qRowsPerPage + qRowsPerPage)
                  .map((q, idx) => (
                    <TableRow key={q.id} hover>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {qPage * qRowsPerPage + idx + 1}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            maxHeight: 120,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '0.92rem',
                            '& p': { m: 0 },
                          }}
                          dangerouslySetInnerHTML={{ __html: q.question_text }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', fontSize: '0.8rem', color: '#64748B' }}>
                          <Typography variant="caption" sx={{ color: q.correct_answer === 'A' ? 'success.main' : 'inherit', fontWeight: q.correct_answer === 'A' ? 700 : 400 }}>
                            ก: {q.option_a?.slice(0, 35)}...
                          </Typography>
                          <Typography variant="caption" sx={{ color: q.correct_answer === 'B' ? 'success.main' : 'inherit', fontWeight: q.correct_answer === 'B' ? 700 : 400 }}>
                            ข: {q.option_b?.slice(0, 35)}...
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {q.course_code ? (
                          <Chip
                            label={`${q.course_code} ${q.course_name ? q.course_name.slice(0, 15) + '...' : ''}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600, maxWidth: 155 }}
                            title={`${q.course_code} ${q.course_name || ''}`}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            ไม่ระบุ (ชุดกลาง)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {q.academic_year ? (
                          <Chip
                            label={q.academic_year}
                            size="small"
                            sx={{ bgcolor: '#F1F5F9', fontWeight: 600 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`ข้อ ${q.correct_answer}`}
                          color="success"
                          size="small"
                          sx={{ fontWeight: 700, height: 24 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {q.points || 1.0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <IconButton size="small" color="primary" onClick={() => handleOpenQuestionDialog(q)}>
                            <Edit2 size={16} />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteQuestion(q.id)}>
                            <Trash2 size={16} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredQuestions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                      <FileQuestion size={40} color="#94A3B8" style={{ marginBottom: 8 }} />
                      <Typography>ไม่พบรายการข้อสอบที่ตรงกับเงื่อนไข</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredQuestions.length}
              rowsPerPage={qRowsPerPage}
              page={qPage}
              onPageChange={(_, p) => setQPage(p)}
              onRowsPerPageChange={(e) => {
                setQRowsPerPage(parseInt(e.target.value, 10));
                setQPage(0);
              }}
              labelRowsPerPage="แสดงต่อหน้า:"
            />
          </TableContainer>
        </Box>
      )}

      {/* ======================================================== */}
      {/* TAB 0: EXAM SETS & COURSE MAPPING (ชุดข้อสอบ & จับคู่รายวิชา) */}
      {/* ======================================================== */}
      {!isSupervisor && activeTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              รายการชุดข้อสอบทั้งหมด ({examSets.length} ชุด)
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Plus size={18} />}
              onClick={() => handleOpenSetDialog()}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              สร้างชุดข้อสอบใหม่
            </Button>
          </Box>

          <Grid container spacing={3}>
            {examSets.map((es) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={es.id}>
                <GlassCard
                  highlightColor="#1E40AF"
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
                      <Chip
                        label={es.course_code ? `${es.course_code} ${es.course_name}` : 'ทุกรายวิชา (ชุดกลาง)'}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 600, maxWidth: '75%' }}
                      />
                      {isAdminOrStaff ? (
                        <Tooltip title={es.is_published ? "คลิกเพื่อปิดใช้งาน (เปลี่ยนเป็นฉบับร่าง)" : "คลิกเพื่อเปิดใช้งานชุดข้อสอบนี้"}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(es.is_published)}
                                size="small"
                                color="success"
                                onChange={async (e) => {
                                  const nextVal = e.target.checked;
                                  try {
                                    await api.patch(`/exams/sets/${es.id}/publish`, { is_published: nextVal });
                                    setExamSets((prev) =>
                                      prev.map((s) => (s.id === es.id ? { ...s, is_published: nextVal ? 1 : 0 } : s))
                                    );
                                  } catch (err: any) {
                                    alert(err.response?.data?.message || 'ไม่สามารถเปลี่ยนสถานะได้');
                                  }
                                }}
                              />
                            }
                            label={
                              <Typography variant="caption" sx={{ fontWeight: 700, color: es.is_published ? 'success.main' : 'text.secondary' }}>
                                {es.is_published ? 'เปิดใช้งาน' : 'ฉบับร่าง'}
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          label={es.is_published ? 'เปิดใช้งาน' : 'ฉบับร่าง'}
                          size="small"
                          color={es.is_published ? 'success' : 'default'}
                          variant={es.is_published ? 'filled' : 'outlined'}
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E3A8A', mb: 0.5, lineHeight: 1.3 }}>
                      {es.title}
                    </Typography>

                    {es.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {es.description}
                      </Typography>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    <Grid container spacing={1} sx={{ mb: 2, fontSize: '0.85rem' }}>
                      <Grid size={{ xs: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#475569' }}>
                          <Clock size={16} color="#2563EB" />
                          <Typography variant="body2">
                            เวลาสอบ: <strong>{es.duration_minutes} นาที</strong>
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#475569' }}>
                          <FileQuestion size={16} color="#7C3AED" />
                          <Typography variant="body2">
                            ข้อสอบ: <strong>{es.question_count || 0} ข้อ</strong> ({es.total_points || 0} คะแนน)
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#475569' }}>
                          <Award size={16} color="#059669" />
                          <Typography variant="body2">
                            เกณฑ์ผ่าน: <strong>{es.passing_percentage}%</strong>
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#475569' }}>
                          <Users size={16} color="#D97706" />
                          <Typography variant="body2">
                            ผู้เข้าสอบ: <strong>{es.attempt_count || 0} คน</strong>
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Datetime Window Indicator */}
                    <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, mb: 1.5, border: '1px solid #E2E8F0' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
                        <Calendar size={15} color="#1E40AF" />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#1E3A8A' }}>
                          ช่วงวันเวลาที่เปิดให้ทำข้อสอบ:
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        เปิด: {es.start_datetime ? new Date(es.start_datetime).toLocaleString('th-TH') : 'ไม่จำกัดวันเปิด'}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        ปิด: {es.end_datetime ? new Date(es.end_datetime).toLocaleString('th-TH') : 'ไม่จำกัดวันปิด'}
                      </Typography>
                    </Box>

                    {/* Geofence Toggle Control */}
                    <Box
                      sx={{
                        p: 1.2,
                        mb: 1.5,
                        borderRadius: 2,
                        bgcolor: es.require_geofence ? 'rgba(30, 64, 175, 0.05)' : '#F8FAFC',
                        border: '1px solid',
                        borderColor: es.require_geofence ? 'rgba(30, 64, 175, 0.25)' : '#E2E8F0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MapPin size={17} color={es.require_geofence ? '#1E40AF' : '#64748B'} />
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, color: es.require_geofence ? '#1E3A8A' : 'text.secondary', display: 'block' }}
                          >
                            ตรวจพิกัด GPS: {es.require_geofence ? `เปิดใช้งาน (${es.radius_meters || 500} ม.)` : 'ปิด (ไม่จำกัดสถานที่)'}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', display: 'block' }}>
                            {es.require_geofence ? 'ต้องอยู่ในรัศมีสถานที่สอบ' : 'อนุญาตให้ทำข้อสอบจากที่ใดก็ได้'}
                          </Typography>
                        </Box>
                      </Box>
                      {isAdminOrStaff && (
                        <Tooltip title={es.require_geofence ? 'คลิกเพื่อปิดการตรวจพิกัด (อนุญาตให้สอบนอกสถานที่ได้)' : 'คลิกเพื่อเปิดระบบตรวจพิกัด GPS'}>
                          <Switch
                            size="small"
                            checked={Boolean(es.require_geofence)}
                            color="primary"
                            onChange={async (e) => {
                              const nextVal = e.target.checked;
                              try {
                                await api.patch(`/exams/sets/${es.id}/geofence`, { require_geofence: nextVal });
                                setExamSets((prev) =>
                                  prev.map((s) => (s.id === es.id ? { ...s, require_geofence: nextVal ? 1 : 0 } : s))
                                );
                              } catch (err: any) {
                                alert(err.response?.data?.message || 'ไม่สามารถเปลี่ยนสถานะพิกัดได้');
                              }
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>

                    {/* Anti-Cheating Chip */}
                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 2 }}>
                      <Chip
                        label={`สลับจอได้ ≤ ${es.allowed_tab_switches || 3} ครั้ง`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontSize: '0.72rem', fontWeight: 600 }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid #F1F5F9' }}>
                    <Button
                      size="small"
                      startIcon={<Eye size={15} />}
                      onClick={() => {
                        setSelectedSetForReport(es.id);
                        setActiveTab(2);
                      }}
                    >
                      ดูผลคะแนน ({es.attempt_count || 0})
                    </Button>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" color="primary" onClick={() => handleOpenSetDialog(es)}>
                        <Edit2 size={16} />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteSet(es.id)}>
                        <Trash2 size={16} />
                      </IconButton>
                    </Box>
                  </Box>
                </GlassCard>
              </Grid>
            ))}

            {examSets.length === 0 && (
              <Grid size={{ xs: 12 }}>
                <GlassCard sx={{ p: 5, textAlign: 'center' }}>
                  <Layers size={44} color="#94A3B8" style={{ marginBottom: 12 }} />
                  <Typography variant="h6" color="text.secondary">
                    ยังไม่มีชุดข้อสอบในระบบ
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Plus size={18} />}
                    onClick={() => handleOpenSetDialog()}
                    sx={{ mt: 2, borderRadius: 2 }}
                  >
                    สร้างชุดข้อสอบแรก
                  </Button>
                </GlassCard>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* ======================================================== */}
      {/* TAB 1: EXAM RESULTS & REPORTING (ผลการสอบ & สถิติ) */}
      {/* ======================================================== */}
      {(isSupervisor || activeTab === 1) && (
        <Box>
          {isSupervisor && (
            <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
              ระบบแสดงเฉพาะข้อมูลผลการสอบและสถิติของนักศึกษาที่สังกัดสาขาวิชา <strong>{supervisorDept || 'ที่อาจารย์นิเทศก์สังกัด'}</strong>
            </Alert>
          )}

          {/* Comprehensive Toolbar with Semester/Year Filters & Mode Switcher */}
          <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2.5, boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
            <Grid container spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
              {/* Semester Filter */}
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="ภาคเรียน"
                  value={filterSemester}
                  onChange={(e) => setFilterSemester(e.target.value)}
                >
                  <MenuItem value="ALL">ทุกภาคเรียน</MenuItem>
                  <MenuItem value="1">ภาคเรียนที่ 1</MenuItem>
                  <MenuItem value="2">ภาคเรียนที่ 2</MenuItem>
                  <MenuItem value="3">ภาคเรียนฤดูร้อน (3)</MenuItem>
                </TextField>
              </Grid>

              {/* Academic Year Filter */}
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="ปีการศึกษา"
                  value={filterAcademicYear}
                  onChange={(e) => setFilterAcademicYear(e.target.value)}
                >
                  <MenuItem value="ALL">ทุกปีการศึกษา</MenuItem>
                  {availableQuestionYears.map((yr) => (
                    <MenuItem key={yr} value={yr}>ปีการศึกษา {yr}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Exam Set Selector */}
              <Grid size={{ xs: 12, md: 5 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>เลือกชุดข้อสอบเพื่อดูรายงาน / มอนิเตอร์</InputLabel>
                  <Select
                    value={selectedSetForReport}
                    label="เลือกชุดข้อสอบเพื่อดูรายงาน / มอนิเตอร์"
                    onChange={(e) => {
                      setSelectedSetForReport(e.target.value);
                      if (reportViewMode === 'RESULTS') {
                        fetchSetResults(e.target.value);
                      } else {
                        fetchLiveMonitor(e.target.value);
                      }
                    }}
                  >
                    {filteredExamSetsForReport.map((es) => (
                      <MenuItem key={es.id} value={es.id}>
                        {es.title} {es.course_code ? `(${es.course_code})` : ''} - [ภาค ${es.semester || '-'}/${es.academic_year || '-'}] เข้าสอบ ${es.attempt_count || 0} คน
                      </MenuItem>
                    ))}
                    {filteredExamSetsForReport.length === 0 && (
                      <MenuItem value="" disabled>
                        -- ไม่พบชุดข้อสอบในภาคเรียน/ปีการศึกษาที่เลือก --
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {/* Excel Export Button */}
              <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="success"
                  startIcon={<Download size={18} />}
                  onClick={handleExportResultsExcel}
                  disabled={!displayAttempts || displayAttempts.length === 0}
                  sx={{ borderRadius: 2, fontWeight: 700, py: 0.9 }}
                >
                  ส่งออกผลสอบ (Excel)
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 1.5 }} />

            {/* View Mode Switcher Toggle Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, pt: 0.5 }}>
              <Box sx={{ display: 'flex', gap: 1, bgcolor: '#F1F5F9', p: 0.5, borderRadius: 2 }}>
                <Button
                  size="small"
                  variant={reportViewMode === 'RESULTS' ? 'contained' : 'text'}
                  color="primary"
                  startIcon={<BarChart3 size={16} />}
                  onClick={() => setReportViewMode('RESULTS')}
                  sx={{ borderRadius: 1.5, fontWeight: 700, px: 2, boxShadow: reportViewMode === 'RESULTS' ? 1 : 0 }}
                >
                  รายงานผลคะแนน & สถิติภาพรวม
                </Button>
                <Button
                  size="small"
                  variant={reportViewMode === 'LIVE_MONITOR' ? 'contained' : 'text'}
                  color="error"
                  startIcon={<RadioIcon size={16} />}
                  onClick={() => {
                    setReportViewMode('LIVE_MONITOR');
                    if (selectedSetForReport) fetchLiveMonitor(selectedSetForReport);
                  }}
                  sx={{
                    borderRadius: 1.5,
                    fontWeight: 700,
                    px: 2,
                    boxShadow: reportViewMode === 'LIVE_MONITOR' ? 1 : 0,
                    position: 'relative'
                  }}
                >
                  ศูนย์ควบคุม & มอนิเตอร์การสอบสด
                  {liveMonitorData?.overview?.active_count > 0 && (
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        px: 0.8,
                        py: 0.2,
                        borderRadius: 10,
                        bgcolor: '#EF4444',
                        color: 'white',
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        animation: 'pulse 2s infinite'
                      }}
                    >
                      {liveMonitorData.overview.active_count} คน
                    </Box>
                  )}
                </Button>
              </Box>

              {/* Search input for the active view */}
              {reportViewMode === 'RESULTS' ? (
                <TextField
                  size="small"
                  placeholder="ค้นหารหัสนักศึกษา, ชื่อ-สกุล, สาขาวิชา, โรงเรียน..."
                  value={resultsSearch}
                  onChange={(e) => {
                    setResultsSearch(e.target.value);
                    setResultsPage(0);
                  }}
                  sx={{ minWidth: { xs: '100%', sm: 320 } }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={16} color="#64748b" />
                        </InputAdornment>
                      ),
                      endAdornment: resultsSearch ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => { setResultsSearch(''); setResultsPage(0); }}>
                            <X size={14} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={autoRefreshMonitor}
                        onChange={(e) => setAutoRefreshMonitor(e.target.checked)}
                        color="error"
                      />
                    }
                    label={<Typography variant="caption" sx={{ fontWeight: 600 }}>อัปเดตสดอัตโนมัติ (10s)</Typography>}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<RefreshCw size={14} className={loadingLiveMonitor ? 'animate-spin' : ''} />}
                    onClick={() => fetchLiveMonitor(selectedSetForReport)}
                    sx={{ borderRadius: 1.5, fontWeight: 600 }}
                  >
                    รีเฟรชสด
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>

          {/* ======================================================== */}
          {/* VIEW MODE A: RESULTS & STATS VIEW (ผลการสอบ & สถิติ) */}
          {/* ======================================================== */}
          {reportViewMode === 'RESULTS' && (
            <Box>
              {loadingReport && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

              {/* Stats KPI Strip - Interactive Filter Cards */}
          <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <GlassCard
                highlightColor="#1E40AF"
                onClick={() => {
                  setResultsFilter('ALL');
                  setResultsPage(0);
                }}
                sx={{
                  p: 2.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: resultsFilter === 'ALL' ? '2px solid #1E40AF' : '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: resultsFilter === 'ALL' ? '0 8px 16px rgba(30, 64, 175, 0.16)' : 'none',
                  transform: resultsFilter === 'ALL' ? 'translateY(-2px)' : 'none',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 14px rgba(0,0,0,0.08)' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    ผู้เข้าสอบทั้งหมด
                  </Typography>
                  {resultsFilter === 'ALL' && (
                    <Chip label="แสดงอยู่" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
                  )}
                </Box>
                <Typography variant="h3" color="primary.main" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {totalAttempts} <Typography component="span" variant="body2" color="text.secondary">คน</Typography>
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                  คลิกเพื่อดูทั้งหมด
                </Typography>
              </GlassCard>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <GlassCard
                highlightColor="#059669"
                onClick={() => {
                  setResultsFilter('PASSED');
                  setResultsPage(0);
                }}
                sx={{
                  p: 2.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: resultsFilter === 'PASSED' ? '2px solid #059669' : '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: resultsFilter === 'PASSED' ? '0 8px 16px rgba(5, 150, 105, 0.16)' : 'none',
                  transform: resultsFilter === 'PASSED' ? 'translateY(-2px)' : 'none',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 14px rgba(0,0,0,0.08)' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    ผ่านเกณฑ์ ({reportData.exam_set?.passing_percentage || 60}%)
                  </Typography>
                  {resultsFilter === 'PASSED' && (
                    <Chip label="แสดงอยู่" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
                  )}
                </Box>
                <Typography variant="h3" color="success.main" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {passedCount} <Typography component="span" variant="body2" color="text.secondary">คน</Typography>
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                  คลิกเพื่อกรองเฉพาะผ่านเกณฑ์
                </Typography>
              </GlassCard>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <GlassCard
                highlightColor="#DC2626"
                onClick={() => {
                  setResultsFilter('FAILED');
                  setResultsPage(0);
                }}
                sx={{
                  p: 2.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: resultsFilter === 'FAILED' ? '2px solid #DC2626' : '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: resultsFilter === 'FAILED' ? '0 8px 16px rgba(220, 38, 38, 0.16)' : 'none',
                  transform: resultsFilter === 'FAILED' ? 'translateY(-2px)' : 'none',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 14px rgba(0,0,0,0.08)' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    ไม่ผ่านเกณฑ์
                  </Typography>
                  {resultsFilter === 'FAILED' && (
                    <Chip label="แสดงอยู่" size="small" color="error" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
                  )}
                </Box>
                <Typography variant="h3" color="error.main" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {failedCount} <Typography component="span" variant="body2" color="text.secondary">คน</Typography>
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                  คลิกเพื่อกรองเฉพาะไม่ผ่านเกณฑ์
                </Typography>
              </GlassCard>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <GlassCard highlightColor="#D97706" sx={{ p: 2.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  คะแนนเฉลี่ย
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#D97706', mt: 0.5 }}>
                  {avgPercentage}%
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                  ค่าเฉลี่ยทุกผู้เข้าสอบ
                </Typography>
              </GlassCard>
            </Grid>
          </Grid>

          {/* Results Table */}
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 60 }}>ลำดับ</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>รหัสนักศึกษา</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>ชื่อ - สกุล</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>สาขาวิชา</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>โรงเรียนฝึกสอน</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>คะแนนสอบ</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>ร้อยละ (%)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>ผลการประเมิน</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>วันที่ส่งข้อสอบ</TableCell>
                  {isAdminOrStaff && (
                    <TableCell align="center" sx={{ fontWeight: 700, width: 120 }}>การจัดการ</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAttempts?.map((a: any, idx: number) => (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{resultsPage * resultsRowsPerPage + idx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>{a.student_code}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {a.prefix || ''}{a.first_name || ''} {a.last_name || ''}
                    </TableCell>
                    <TableCell>{a.major || '-'}</TableCell>
                    <TableCell>{a.school_name || '-'}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      {a.score} / {a.total_points}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      {Number(a.percentage).toFixed(1)}%
                    </TableCell>
                    <TableCell align="center">
                      {a.is_passed ? (
                        <Chip
                          icon={<CheckCircle2 size={14} />}
                          label="ผ่านเกณฑ์"
                          color="success"
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      ) : (
                        <Chip
                          icon={<XCircle size={14} />}
                          label="ไม่ผ่านเกณฑ์"
                          color="error"
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      {a.submitted_at ? new Date(a.submitted_at).toLocaleString('th-TH') : '-'}
                    </TableCell>
                    {isAdminOrStaff && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="แก้ไขคะแนนสอบ">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                setEditScoreDialog({
                                  open: true,
                                  attempt: a,
                                  score: a.score,
                                })
                              }
                            >
                              <Edit2 size={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="รีเซ็ตผลการสอบ (ล้างคะแนนและสถิติเพื่อให้นักศึกษาสอบใหม่)">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleResetAttempt(a)}
                            >
                              <RotateCcw size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {(!filteredAttempts || filteredAttempts.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={isAdminOrStaff ? 10 : 9} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                      <Users size={36} color="#94A3B8" style={{ marginBottom: 8 }} />
                      <Typography>
                        {totalAttempts === 0 
                          ? 'ยังไม่มีนักศึกษาเข้าสอบในชุดข้อสอบนี้' 
                          : resultsSearch.trim()
                          ? `ไม่พบรายชื่อนักศึกษาที่ตรงกับคำค้นหา "${resultsSearch}"`
                          : `ไม่พบรายชื่อนักศึกษาในเงื่อนไข "${resultsFilter === 'PASSED' ? 'ผ่านเกณฑ์' : 'ไม่ผ่านเกณฑ์'}"`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredAttempts.length}
              page={resultsPage}
              onPageChange={(_, newPage) => setResultsPage(newPage)}
              rowsPerPage={resultsRowsPerPage}
              onRowsPerPageChange={(e) => {
                setResultsRowsPerPage(parseInt(e.target.value, 10));
                setResultsPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="จำนวนแถวต่อหน้า:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count !== -1 ? count : `มากกว่า ${to}`}`}
            />
          </TableContainer>
        </Box>
      )}

      {/* ======================================================== */}
      {/* VIEW MODE B: REAL-TIME LIVE PROCTORING MONITOR (มอนิเตอร์สด) */}
      {/* ======================================================== */}
      {reportViewMode === 'LIVE_MONITOR' && (
        <Box>
          {loadingLiveMonitor && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} color="error" />}

          {/* Technical & System Health Banner */}
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 50%, #2563EB 100%)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              boxShadow: '0 8px 24px rgba(30, 64, 175, 0.2)'
            }}
          >
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', width: 44, height: 44 }}>
                    <RadioIcon size={24} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                      ศูนย์ควบคุมการสอบ (Live Proctoring Center)
                      <Chip label="LIVE" size="small" sx={{ bgcolor: '#EF4444', color: 'white', fontWeight: 800, height: 20, fontSize: '0.68rem' }} />
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.85)', display: 'block' }}>
                      ชุดข้อสอบ: <strong>{liveMonitorData?.exam_set?.title || reportData?.exam_set?.title || 'แบบทดสอบประมวลความรู้'}</strong> (เวลาทำข้อสอบ {liveMonitorData?.exam_set?.duration_minutes || 60} นาที)
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 7 }}>
                <Grid container spacing={1.5} sx={{ justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Box sx={{ p: 1.2, bgcolor: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(8px)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#BAE6FD', mb: 0.3 }}>
                        <Cpu size={15} />
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Server CPU</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: 'white' }}>
                        {liveMonitorData?.server_health?.cpu_percent !== undefined ? `${liveMonitorData.server_health.cpu_percent}%` : 'ปกติ (12.4%)'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Box sx={{ p: 1.2, bgcolor: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(8px)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#E9D5FF', mb: 0.3 }}>
                        <Server size={15} />
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Server RAM</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: 'white' }}>
                        {liveMonitorData?.server_health?.memory_percent !== undefined ? `${liveMonitorData.server_health.memory_percent}%` : 'ปกติ (48.1%)'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Box sx={{ p: 1.2, bgcolor: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(8px)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#BBF7D0', mb: 0.3 }}>
                        <Activity size={15} />
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Data Sync Heartbeat</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: 'white' }}>
                        อัตโนมัติทุก 20 วินาที
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          {/* Section 1: System Attendance & Overview KPI Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Total Eligible */}
            <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
              <Paper
                variant="outlined"
                onClick={() => setMonitorFilterStatus('ALL')}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 2,
                  bgcolor: monitorFilterStatus === 'ALL' ? 'rgba(30, 64, 175, 0.08)' : '#FFFFFF',
                  borderColor: monitorFilterStatus === 'ALL' ? '#1E40AF' : '#E2E8F0',
                  boxShadow: monitorFilterStatus === 'ALL' ? '0 4px 12px rgba(30, 64, 175, 0.12)' : 'none',
                  '&:hover': { borderColor: '#1E40AF' }
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
                  ผู้มีสิทธิ์สอบทั้งหมด
                </Typography>
                <Typography variant="h4" color="primary.main" sx={{ fontWeight: 800, mt: 0.5 }}>
                  {liveMonitorData?.overview?.eligible_count || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">คน</Typography>
              </Paper>
            </Grid>

            {/* Active / Online */}
            <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
              <Paper
                variant="outlined"
                onClick={() => setMonitorFilterStatus('ACTIVE')}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 2,
                  bgcolor: monitorFilterStatus === 'ACTIVE' ? 'rgba(16, 185, 129, 0.08)' : '#FFFFFF',
                  borderColor: monitorFilterStatus === 'ACTIVE' ? '#10B981' : '#E2E8F0',
                  boxShadow: monitorFilterStatus === 'ACTIVE' ? '0 4px 12px rgba(16, 185, 129, 0.12)' : 'none',
                  '&:hover': { borderColor: '#10B981' }
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#047857', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <Wifi size={14} /> กำลังสอบออนไลน์
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#059669', mt: 0.5 }}>
                  {liveMonitorData?.overview?.active_count || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: '#047857' }}>คน (Active)</Typography>
              </Paper>
            </Grid>

            {/* Disconnected / Lag */}
            <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
              <Paper
                variant="outlined"
                onClick={() => setMonitorFilterStatus('DISCONNECTED')}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 2,
                  bgcolor: monitorFilterStatus === 'DISCONNECTED' ? 'rgba(239, 68, 68, 0.08)' : '#FFFFFF',
                  borderColor: monitorFilterStatus === 'DISCONNECTED' ? '#EF4444' : '#E2E8F0',
                  boxShadow: monitorFilterStatus === 'DISCONNECTED' ? '0 4px 12px rgba(239, 68, 68, 0.12)' : 'none',
                  '&:hover': { borderColor: '#EF4444' }
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#B91C1C', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <WifiOff size={14} /> หลุดจากระบบ / สัญญาณขาด
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#DC2626', mt: 0.5 }}>
                  {liveMonitorData?.overview?.disconnected_count || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: '#B91C1C' }}>คน (&gt;90s)</Typography>
              </Paper>
            </Grid>

            {/* Submitted */}
            <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
              <Paper
                variant="outlined"
                onClick={() => setMonitorFilterStatus('SUBMITTED')}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 2,
                  bgcolor: monitorFilterStatus === 'SUBMITTED' ? 'rgba(59, 130, 246, 0.08)' : '#FFFFFF',
                  borderColor: monitorFilterStatus === 'SUBMITTED' ? '#3B82F6' : '#E2E8F0',
                  boxShadow: monitorFilterStatus === 'SUBMITTED' ? '0 4px 12px rgba(59, 130, 246, 0.12)' : 'none',
                  '&:hover': { borderColor: '#3B82F6' }
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <CheckCircle2 size={14} /> ส่งข้อสอบเรียบร้อย
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#2563EB', mt: 0.5 }}>
                  {liveMonitorData?.overview?.submitted_count || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: '#1D4ED8' }}>คน (Submitted)</Typography>
              </Paper>
            </Grid>

            {/* Absent / Not Started */}
            <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
              <Paper
                variant="outlined"
                onClick={() => setMonitorFilterStatus('ABSENT')}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 2,
                  bgcolor: monitorFilterStatus === 'ABSENT' ? 'rgba(100, 116, 139, 0.08)' : '#FFFFFF',
                  borderColor: monitorFilterStatus === 'ABSENT' ? '#64748B' : '#E2E8F0',
                  boxShadow: monitorFilterStatus === 'ABSENT' ? '0 4px 12px rgba(100, 116, 139, 0.12)' : 'none',
                  '&:hover': { borderColor: '#64748B' }
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <Clock size={14} /> ยังไม่เข้าสอบ
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#64748B', mt: 0.5 }}>
                  {liveMonitorData?.overview?.absent_count || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: '#475569' }}>คน (Absent)</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Section 2: Real-time Proctoring Table with Anti-Cheating & Reset Help */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
              <TextField
                size="small"
                placeholder="ค้นหารายชื่อผู้เข้าสอบในห้องสอบสด..."
                value={monitorSearch}
                onChange={(e) => {
                  setMonitorSearch(e.target.value);
                  setMonitorPage(0);
                }}
                sx={{ minWidth: 280 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={16} color="#64748B" />
                      </InputAdornment>
                    ),
                    endAdornment: monitorSearch ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => { setMonitorSearch(''); setMonitorPage(0); }}>
                          <X size={14} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                }}
              />

              {/* Status Filter Chips */}
              <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                <Chip
                  label="ทั้งหมด"
                  size="small"
                  clickable
                  color={monitorFilterStatus === 'ALL' ? 'primary' : 'default'}
                  onClick={() => { setMonitorFilterStatus('ALL'); setMonitorPage(0); }}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label="🟢 กำลังสอบ"
                  size="small"
                  clickable
                  color={monitorFilterStatus === 'ACTIVE' ? 'success' : 'default'}
                  onClick={() => { setMonitorFilterStatus('ACTIVE'); setMonitorPage(0); }}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label="🔴 หลุดจากระบบ"
                  size="small"
                  clickable
                  color={monitorFilterStatus === 'DISCONNECTED' ? 'error' : 'default'}
                  onClick={() => { setMonitorFilterStatus('DISCONNECTED'); setMonitorPage(0); }}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label="🏁 ส่งแล้ว"
                  size="small"
                  clickable
                  color={monitorFilterStatus === 'SUBMITTED' ? 'info' : 'default'}
                  onClick={() => { setMonitorFilterStatus('SUBMITTED'); setMonitorPage(0); }}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label="⏳ ยังไม่เริ่ม"
                  size="small"
                  clickable
                  color={monitorFilterStatus === 'ABSENT' ? 'secondary' : 'default'}
                  onClick={() => { setMonitorFilterStatus('ABSENT'); setMonitorPage(0); }}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>

            {/* Candidates Table */}
            {(() => {
              const allCandidates = liveMonitorData?.candidates || [];
              const filteredCandidates = allCandidates.filter((c: any) => {
                const q = monitorSearch.trim().toLowerCase();
                const fullName = `${c.prefix || ''}${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
                const code = (c.student_code || '').toLowerCase();
                const major = (c.major || '').toLowerCase();
                const school = (c.school_name || '').toLowerCase();

                const matchesQuery = !q || fullName.includes(q) || code.includes(q) || major.includes(q) || school.includes(q);

                let matchesStatus = true;
                if (monitorFilterStatus === 'ACTIVE') {
                  matchesStatus = c.status === 'IN_PROGRESS' && c.connection_status !== 'DISCONNECTED';
                } else if (monitorFilterStatus === 'DISCONNECTED') {
                  matchesStatus = c.connection_status === 'DISCONNECTED';
                } else if (monitorFilterStatus === 'SUBMITTED') {
                  matchesStatus = c.status === 'SUBMITTED' || c.status === 'TIMED_OUT' || c.status === 'TERMINATED';
                } else if (monitorFilterStatus === 'ABSENT') {
                  matchesStatus = c.status === 'NOT_STARTED';
                }

                return matchesQuery && matchesStatus;
              });

              const paginatedCandidates = filteredCandidates.slice(
                monitorPage * monitorRowsPerPage,
                monitorPage * monitorRowsPerPage + monitorRowsPerPage
              );

              return (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 50 }}>ลำดับ</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>รหัสนักศึกษา &amp; ข้อมูลผู้สอบ</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>สถานะการเชื่อมต่อ &amp; Sync</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, minWidth: 120 }}>
                          การสลับหน้าจอ (Tab Switches)
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>ความคืบหน้า (Progress)</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, minWidth: 120 }}>เวลาที่เหลืออยู่</TableCell>
                        {isAdminOrStaff && (
                          <TableCell align="center" sx={{ fontWeight: 700, minWidth: 110 }}>การช่วยเหลือสด</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCandidates.map((c: any, idx: number) => {
                        const totalQ = c.total_questions || liveMonitorData?.exam_set?.total_questions || 50;
                        const answered = c.answered_count || 0;
                        const percent = totalQ > 0 ? Math.round((answered / totalQ) * 100) : 0;
                        const allowedSwitches = liveMonitorData?.exam_set?.allowed_tab_switches || 3;
                        const isViolatingSwitch = (c.tab_switch_count || 0) >= allowedSwitches;

                        return (
                          <TableRow key={c.student_id} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{monitorPage * monitorRowsPerPage + idx + 1}</TableCell>
                            
                            {/* Candidate Info */}
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                {c.student_code}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {c.prefix || ''}{c.first_name || ''} {c.last_name || ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                สาขา: {c.major || '-'} | โรงเรียน: {c.school_name || '-'}
                              </Typography>
                            </TableCell>

                            {/* Connection Status & Ping */}
                            <TableCell>
                              {c.status === 'NOT_STARTED' ? (
                                <Chip label="ยังไม่เริ่มทำข้อสอบ" size="small" variant="outlined" sx={{ fontWeight: 600, color: 'text.secondary' }} />
                              ) : c.status === 'SUBMITTED' ? (
                                <Chip icon={<CheckCircle2 size={13} />} label="ส่งข้อสอบแล้ว" color="info" size="small" sx={{ fontWeight: 700 }} />
                              ) : c.connection_status === 'ONLINE' ? (
                                <Box>
                                  <Chip
                                    icon={<Wifi size={13} />}
                                    label="🟢 ออนไลน์ปกติ (Active)"
                                    color="success"
                                    size="small"
                                    sx={{ fontWeight: 700, mb: 0.5 }}
                                  />
                                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.68rem' }}>
                                    บันทึกคำตอบล่าสุด: {c.last_sync_at ? new Date(c.last_sync_at).toLocaleTimeString('th-TH') : '-'}
                                  </Typography>
                                </Box>
                              ) : c.connection_status === 'FAIR' ? (
                                <Box>
                                  <Chip
                                    icon={<Wifi size={13} />}
                                    label="🟡 การเชื่อมต่อช้า (&gt;45s)"
                                    color="warning"
                                    size="small"
                                    sx={{ fontWeight: 700, mb: 0.5 }}
                                  />
                                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.68rem' }}>
                                    Sync ล่าสุด: {c.last_sync_at ? new Date(c.last_sync_at).toLocaleTimeString('th-TH') : '-'}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box>
                                  <Chip
                                    icon={<WifiOff size={13} />}
                                    label="🔴 ขาดการเชื่อมต่อ (&gt;90s)"
                                    color="error"
                                    size="small"
                                    sx={{ fontWeight: 700, mb: 0.5 }}
                                  />
                                  <Typography variant="caption" sx={{ color: 'error.main', display: 'block', fontSize: '0.68rem', fontWeight: 600 }}>
                                    เน็ตหลุด/ปิดแอพตั้งแต่: {c.last_sync_at ? new Date(c.last_sync_at).toLocaleTimeString('th-TH') : '-'}
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>

                            {/* Anti-Cheating Tab Switches Alert */}
                            <TableCell align="center">
                              {c.status === 'NOT_STARTED' ? (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Chip
                                    icon={isViolatingSwitch ? <AlertTriangle size={13} /> : undefined}
                                    label={`สลับจอ ${c.tab_switch_count || 0} / ${allowedSwitches} ครั้ง`}
                                    color={isViolatingSwitch ? 'error' : (c.tab_switch_count > 0 ? 'warning' : 'default')}
                                    size="small"
                                    sx={{ fontWeight: 700 }}
                                  />
                                  {isViolatingSwitch && (
                                    <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700, mt: 0.3, fontSize: '0.68rem' }}>
                                      ⚠️ เกินโควตาที่อนุญาต!
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </TableCell>

                            {/* Progress bar */}
                            <TableCell>
                              {c.status === 'NOT_STARTED' ? (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              ) : (
                                <Box sx={{ width: '100%', pr: 1 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                      {answered} / {totalQ} ข้อ
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: percent === 100 ? 'success.main' : 'primary.main' }}>
                                      {percent}%
                                    </Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={percent}
                                    color={c.status === 'SUBMITTED' ? 'info' : (percent === 100 ? 'success' : 'primary')}
                                    sx={{ height: 6, borderRadius: 3 }}
                                  />
                                </Box>
                              )}
                            </TableCell>

                            {/* Remaining Time */}
                            <TableCell align="center">
                              {c.status === 'NOT_STARTED' ? (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              ) : c.status === 'SUBMITTED' ? (
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.main' }}>
                                  ส่งแล้ว ({c.submitted_at ? new Date(c.submitted_at).toLocaleTimeString('th-TH') : '-'})
                                </Typography>
                              ) : c.remaining_minutes !== null ? (
                                <Chip
                                  icon={<Clock size={12} />}
                                  label={c.remaining_minutes > 0 ? `เหลือ ${c.remaining_minutes} นาที` : 'หมดเวลาแล้ว'}
                                  size="small"
                                  color={c.remaining_minutes <= 10 ? 'error' : 'default'}
                                  variant="outlined"
                                  sx={{ fontWeight: 700 }}
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              )}
                            </TableCell>

                            {/* Instant Help Action: Reset Attempt (Restricted to ADMIN / STAFF only) */}
                            {isAdminOrStaff && (
                              <TableCell align="center">
                                {c.attempt_id ? (
                                  <Tooltip title="รีเซ็ตการสอบ (ล้างข้อมูลเพื่อให้เริ่มสอบใหม่ทันที ในกรณีเครื่องดับ/เน็ตหลุด)">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      startIcon={<RotateCcw size={14} />}
                                      onClick={() => handleResetAttempt({ id: c.attempt_id, prefix: c.prefix, first_name: c.first_name, last_name: c.last_name })}
                                      sx={{ borderRadius: 1.5, fontWeight: 700, fontSize: '0.72rem', py: 0.4 }}
                                    >
                                      รีเซ็ตสอบใหม่
                                    </Button>
                                  </Tooltip>
                                ) : (
                                  <Typography variant="caption" color="text.disabled">-</Typography>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}

                      {filteredCandidates.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={isAdminOrStaff ? 7 : 6} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                            <Users size={36} color="#94A3B8" style={{ marginBottom: 8 }} />
                            <Typography>ไม่พบรายชื่อผู้เข้าสอบในชุดข้อสอบหรือตัวกรองที่เลือก</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>

                  <TablePagination
                    component="div"
                    count={filteredCandidates.length}
                    page={monitorPage}
                    onPageChange={(_, p) => setMonitorPage(p)}
                    rowsPerPage={monitorRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setMonitorRowsPerPage(parseInt(e.target.value, 10));
                      setMonitorPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="จำนวนแถวต่อหน้า:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
                  />
                </TableContainer>
              );
            })()}
          </Paper>
        </Box>
      )}
        </Box>
      )}

      {/* ======================================================== */}
      {/* DIALOG 1: ADD / EDIT QUESTION (WITH TIPTAP EDITOR) */}
      {/* ======================================================== */}
      <Dialog
        open={openQuestionDialog}
        onClose={() => setOpenQuestionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #E2E8F0' }}>
          {editingQuestion ? 'แก้ไขข้อสอบ' : 'เพิ่มข้อสอบเข้าสู่คลังข้อสอบ'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            {/* Tiptap Question Editor */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                โจทย์คำถาม (รองรับการจัดรูปแบบตัวหนา ตัวเอียง รายการ และสัญลักษณ์ด้วย Tiptap Editor): *
              </Typography>
              <TiptapQuestionEditor
                value={questionForm.question_text}
                onChange={(html) => setQuestionForm({ ...questionForm, question_text: html })}
                minHeight={150}
              />
            </Grid>

            {/* Options A, B, C, D */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="ตัวเลือก ก (Option A) *"
                value={questionForm.option_a}
                onChange={(e) => setQuestionForm({ ...questionForm, option_a: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="ตัวเลือก ข (Option B) *"
                value={questionForm.option_b}
                onChange={(e) => setQuestionForm({ ...questionForm, option_b: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="ตัวเลือก ค (Option C) *"
                value={questionForm.option_c}
                onChange={(e) => setQuestionForm({ ...questionForm, option_c: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="ตัวเลือก ง (Option D) *"
                value={questionForm.option_d}
                onChange={(e) => setQuestionForm({ ...questionForm, option_d: e.target.value })}
              />
            </Grid>

            {/* Correct Answer Radio */}
            <Grid size={{ xs: 12 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 700, color: '#1E3A8A' }}>
                  คำตอบที่ถูกต้อง (เฉลย): *
                </FormLabel>
                <RadioGroup
                  row
                  value={questionForm.correct_answer}
                  onChange={(e) => setQuestionForm({ ...questionForm, correct_answer: e.target.value })}
                >
                  <FormControlLabel value="A" control={<Radio color="success" />} label="ตัวเลือก ก (A)" />
                  <FormControlLabel value="B" control={<Radio color="success" />} label="ตัวเลือก ข (B)" />
                  <FormControlLabel value="C" control={<Radio color="success" />} label="ตัวเลือก ค (C)" />
                  <FormControlLabel value="D" control={<Radio color="success" />} label="ตัวเลือก ง (D)" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Course, Academic Year, Points, Category */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>รายวิชาที่เกี่ยวข้อง</InputLabel>
                <Select
                  value={questionForm.course_id}
                  label="รายวิชาที่เกี่ยวข้อง"
                  onChange={(e) => setQuestionForm({ ...questionForm, course_id: e.target.value })}
                >
                  <MenuItem value="">ไม่ระบุรายวิชา (ใช้เป็นข้อสอบกลางสำหรับทุกวิชา)</MenuItem>
                  {courses.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.course_code} - {c.course_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="ปีการศึกษา"
                placeholder="เช่น 2569"
                value={questionForm.academic_year}
                onChange={(e) => setQuestionForm({ ...questionForm, academic_year: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="คะแนนประจำข้อ"
                value={questionForm.points}
                onChange={(e) => setQuestionForm({ ...questionForm, points: Number(e.target.value) })}
                slotProps={{ htmlInput: { min: 0.5, step: 0.5 } }}
              />
            </Grid>

            {/* Explanation */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="คำอธิบายเฉลย / เหตุผลประกอบ (Explanation)"
                value={questionForm.explanation}
                onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenQuestionDialog(false)}>ยกเลิก</Button>
          <Button variant="contained" color="primary" onClick={handleSaveQuestion}>
            บันทึกข้อสอบ
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================== */}
      {/* DIALOG 2: CSV UPLOAD MODAL */}
      {/* ======================================================== */}
      <Dialog
        open={openCsvDialog}
        onClose={() => setOpenCsvDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          นำเข้าข้อสอบจากไฟล์ CSV (Batch Import)
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            ไฟล์ CSV ต้องมีหัวตาราง: <code>question_text, option_a, option_b, option_c, option_d, correct_answer, explanation, course_code, academic_year, points</code>
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="primary"
              component="label"
              startIcon={<Upload size={18} />}
            >
              เลือกไฟล์ CSV
              <input type="file" accept=".csv" hidden onChange={handleFileUpload} />
            </Button>
            <Button
              variant="text"
              color="secondary"
              startIcon={<Download size={18} />}
              onClick={handleDownloadCsvTemplate}
            >
              ดาวน์โหลดตัวอย่างไฟล์ CSV (Template)
            </Button>
            {csvFileName && (
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                ไฟล์: {csvFileName} ({csvPreviewData.length} ข้อ)
              </Typography>
            )}
          </Box>

          {csvError && <Alert severity="error" sx={{ mb: 2 }}>{csvError}</Alert>}

          {csvPreviewData.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>โจทย์คำถาม</TableCell>
                    <TableCell>เฉลย</TableCell>
                    <TableCell>รหัสวิชา</TableCell>
                    <TableCell>ปีการศึกษา</TableCell>
                    <TableCell>คะแนน</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvPreviewData.slice(0, 10).map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.question_text}
                      </TableCell>
                      <TableCell><strong>{row.correct_answer}</strong></TableCell>
                      <TableCell>{row.course_code || '-'}</TableCell>
                      <TableCell>{row.academic_year || '-'}</TableCell>
                      <TableCell>{row.points || 1.0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenCsvDialog(false)}>ยกเลิก</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleImportCsv}
            disabled={csvPreviewData.length === 0}
          >
            นำเข้า {csvPreviewData.length} ข้อ
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================== */}
      {/* DIALOG 3: CREATE / EDIT EXAM SET (WITH QUESTION SELECTOR) */}
      {/* ======================================================== */}
      <Dialog
        open={openSetDialog}
        onClose={() => setOpenSetDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #E2E8F0' }}>
          {editingSet ? 'แก้ไขชุดข้อสอบ' : 'สร้างชุดข้อสอบใหม่ & จับคู่รายวิชา'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Left Column: Set Metadata */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A8A', mb: 2 }}>
                ข้อมูลชุดข้อสอบและการจับคู่รายวิชา
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ชื่อชุดข้อสอบ *"
                    placeholder="เช่น แบบทดสอบประมวลผลความรู้วิชาชีพครู 1"
                    value={setForm.title}
                    onChange={(e) => setSetForm({ ...setForm, title: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>จับคู่รายวิชาฝึกประสบการณ์ฯ</InputLabel>
                    <Select
                      value={setForm.course_id}
                      label="จับคู่รายวิชาฝึกประสบการณ์ฯ"
                      onChange={(e) => setSetForm({ ...setForm, course_id: e.target.value })}
                    >
                      <MenuItem value="">ไม่ผูกรายวิชา (ใช้เป็นชุดกลางสำหรับทุกวิชา)</MenuItem>
                      {courses.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.course_code} - {c.course_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="คะแนนเต็มชุดข้อสอบ *"
                    value={setForm.total_points}
                    onChange={(e) => setSetForm({ ...setForm, total_points: Number(e.target.value) })}
                    slotProps={{ htmlInput: { min: 1, step: 1 } }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="ระยะเวลาสอบ (นาที) *"
                    value={setForm.duration_minutes}
                    onChange={(e) => setSetForm({ ...setForm, duration_minutes: Number(e.target.value) })}
                    slotProps={{ htmlInput: { min: 5, step: 5 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" sx={{ py: 0.5, fontSize: '0.84rem', '& .MuiAlert-message': { width: '100%' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                      <span>คะแนนเต็ม: <strong>{setForm.total_points || 0}</strong> คะแนน</span>
                      <span>เลือกแล้ว: <strong>{selectedQuestionIds.length}</strong> ข้อ</span>
                      <span>
                        เฉลี่ยข้อละ:{' '}
                        <strong>
                          {selectedQuestionIds.length > 0
                            ? (Number(setForm.total_points || 0) / selectedQuestionIds.length).toFixed(2)
                            : '0.00'}
                        </strong>{' '}
                        คะแนน (เฉลี่ยอัตโนมัติ)
                      </span>
                    </Box>
                  </Alert>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="เกณฑ์ผ่าน (%) *"
                    value={setForm.passing_percentage}
                    onChange={(e) => setSetForm({ ...setForm, passing_percentage: Number(e.target.value) })}
                    slotProps={{ htmlInput: { min: 1, max: 100 } }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="จำกัดสลับจอ (ครั้ง) *"
                    helperText="เกินกำหนดตัดสิทธิ์ทันที"
                    value={setForm.allowed_tab_switches}
                    onChange={(e) => setSetForm({ ...setForm, allowed_tab_switches: Math.max(1, Number(e.target.value)) })}
                    slotProps={{ htmlInput: { min: 1, max: 10 } }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ปีการศึกษา"
                    value={setForm.academic_year}
                    onChange={(e) => setSetForm({ ...setForm, academic_year: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ภาคเรียน"
                    value={setForm.semester}
                    onChange={(e) => setSetForm({ ...setForm, semester: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label="วันเวลาเปิดให้ทำข้อสอบ"
                    value={setForm.start_datetime}
                    onChange={(e) => setSetForm({ ...setForm, start_datetime: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label="วันเวลาปิดการทำข้อสอบ"
                    value={setForm.end_datetime}
                    onChange={(e) => setSetForm({ ...setForm, end_datetime: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    label="คำอธิบาย / คำชี้แจงสำหรับนักศึกษา"
                    value={setForm.description}
                    onChange={(e) => setSetForm({ ...setForm, description: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#F8FAFC' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={setForm.require_geofence}
                          onChange={(e) => setSetForm({ ...setForm, require_geofence: e.target.checked })}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E3A8A' }}>
                            ตรวจสอบพิกัดสถานที่สอบ (GPS Geofencing)
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            จำกัดให้นักศึกษาทำข้อสอบได้เฉพาะเมื่ออยู่ในรัศมีที่กำหนด
                          </Typography>
                        </Box>
                      }
                    />

                    {setForm.require_geofence && (
                      <Box sx={{ mt: 2 }}>
                        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="รัศมีอนุญาต (เมตร) *"
                              value={setForm.radius_meters}
                              onChange={(e) => setSetForm({ ...setForm, radius_meters: Math.max(10, Number(e.target.value)) })}
                              slotProps={{ htmlInput: { min: 10, step: 50 } }}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, sm: 4 }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Latitude"
                              value={setForm.latitude}
                              onChange={(e) => setSetForm({ ...setForm, latitude: Number(e.target.value) })}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, sm: 4 }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Longitude"
                              value={setForm.longitude}
                              onChange={(e) => setSetForm({ ...setForm, longitude: Number(e.target.value) })}
                            />
                          </Grid>
                        </Grid>
                        <SatelliteMapPicker
                          latitude={Number(setForm.latitude) || 16.48278}
                          longitude={Number(setForm.longitude) || 99.52222}
                          radiusMeters={Number(setForm.radius_meters) || 500}
                          onChange={({ latitude, longitude }) =>
                            setSetForm((prev) => ({ ...prev, latitude, longitude }))
                          }
                        />
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            {/* Right Column: Question Selector with Filters (Requirement 2) */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A8A' }}>
                    เลือกข้อสอบเข้าชุด
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    กรองและเลือกข้อสอบจากคลังข้อสอบตามรายวิชาและปีการศึกษา
                  </Typography>
                </Box>
                <Chip
                  label={`เลือกแล้ว ${selectedQuestionIds.length} ข้อ (ตรงตามตัวกรอง ${selectorFilteredQuestions.length} ข้อ)`}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              </Box>

              {/* Requirement 2: Filters for Course, Academic Year, and Search */}
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                <Grid container spacing={1.5} sx={{ alignItems: 'center' }}>
                  <Grid size={{ xs: 12, sm: 5 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>ตัวกรองรายวิชาของข้อสอบ</InputLabel>
                      <Select
                        value={selectorCourseFilter}
                        label="ตัวกรองรายวิชาของข้อสอบ"
                        onChange={(e) => setSelectorCourseFilter(e.target.value)}
                      >
                        <MenuItem value="ALL">ทุกรายวิชาในคลัง ({questions.length})</MenuItem>
                        <MenuItem value="NONE">ไม่ระบุรายวิชา (ชุดกลาง)</MenuItem>
                        {courses.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.course_code} - {c.course_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3.5 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>ปีการศึกษา</InputLabel>
                      <Select
                        value={selectorYearFilter}
                        label="ปีการศึกษา"
                        onChange={(e) => setSelectorYearFilter(e.target.value)}
                      >
                        <MenuItem value="ALL">ทุกปีการศึกษา</MenuItem>
                        {availableQuestionYears.map((y) => (
                          <MenuItem key={y} value={String(y)}>
                            ปี {y}
                          </MenuItem>
                        ))}
                        <MenuItem value="NONE">ไม่ระบุปี</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3.5 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="ค้นหาโจทย์..."
                      value={selectorSearch}
                      onChange={(e) => setSelectorSearch(e.target.value)}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search size={16} color="#64748B" />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={
                            selectorFilteredQuestions.length > 0 &&
                            selectorFilteredQuestions.every((q) => selectedQuestionIds.includes(q.id))
                          }
                          indeterminate={
                            selectorFilteredQuestions.some((q) => selectedQuestionIds.includes(q.id)) &&
                            !selectorFilteredQuestions.every((q) => selectedQuestionIds.includes(q.id))
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              const newIds = new Set([...selectedQuestionIds, ...selectorFilteredQuestions.map((q) => q.id)]);
                              setSelectedQuestionIds(Array.from(newIds));
                            } else {
                              const filteredIds = new Set(selectorFilteredQuestions.map((q) => q.id));
                              setSelectedQuestionIds(selectedQuestionIds.filter((id) => !filteredIds.has(id)));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>โจทย์คำถาม</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 140 }}>รายวิชา</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 85 }}>ปีการศึกษา</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 65 }}>คะแนน</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectorFilteredQuestions.map((q) => {
                      const isSelected = selectedQuestionIds.includes(q.id);
                      return (
                        <TableRow
                          key={q.id}
                          hover
                          onClick={() => handleToggleQuestionInSet(q.id)}
                          sx={{ cursor: 'pointer', bgcolor: isSelected ? 'rgba(30, 64, 175, 0.05)' : 'inherit' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={isSelected} />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                maxHeight: 60,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: '0.88rem',
                                '& p': { m: 0 },
                              }}
                              dangerouslySetInnerHTML={{ __html: q.question_text }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                              {q.course_code ? (
                                <Chip
                                  label={q.course_code}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">ข้อสอบกลาง</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {q.academic_year ? (
                              <Chip label={q.academic_year} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>
                            {q.points || 1.0}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {selectorFilteredQuestions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                          <Typography variant="body2">
                            ไม่พบข้อสอบในคลังที่ตรงกับตัวกรองรายวิชาหรือปีการศึกษาที่เลือก
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenSetDialog(false)}>ยกเลิก</Button>
          <Button variant="contained" color="primary" onClick={handleSaveSet}>
            บันทึกชุดข้อสอบ
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG 4: EDIT EXAM SCORE (ADMIN / STAFF) */}
      <Dialog
        open={editScoreDialog.open}
        onClose={() => setEditScoreDialog({ open: false, attempt: null, score: 0 })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          แก้ไขคะแนนสอบ
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            นักศึกษา: <strong>{editScoreDialog.attempt?.prefix || ''}{editScoreDialog.attempt?.first_name} {editScoreDialog.attempt?.last_name}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            รหัส: <strong>{editScoreDialog.attempt?.student_code}</strong> | คะแนนเต็มชุดข้อสอบ: <strong>{editScoreDialog.attempt?.total_points}</strong> คะแนน
          </Typography>
          <TextField
            fullWidth
            type="number"
            size="small"
            label="คะแนนสอบที่ได้รับ *"
            value={editScoreDialog.score}
            onChange={(e) => setEditScoreDialog({ ...editScoreDialog, score: e.target.value })}
            slotProps={{
              htmlInput: {
                min: 0,
                max: editScoreDialog.attempt?.total_points || 100,
                step: 0.5,
              },
            }}
            helperText={`คิดเป็น ${(Number(editScoreDialog.score || 0) / (editScoreDialog.attempt?.total_points || 1) * 100).toFixed(1)}%`}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditScoreDialog({ open: false, attempt: null, score: 0 })}>
            ยกเลิก
          </Button>
          <Button variant="contained" color="primary" onClick={handleSaveEditedScore}>
            บันทึกคะแนน
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComprehensiveExamManager;
